import type { BinaryReader, BinaryWriter, Command } from '@am/sim';

/**
 * Wire ids for commands. Append new ones; never renumber once released (it would break replays).
 * Protocol 2 replaced the M0 wall-building commands, which were never released.
 */
const CommandId = {
  buyParcel: 1,
  placeBuilding: 2,
  upgradeBuilding: 3,
  demolishBuilding: 4,
  buildNetwork: 5,
  removeNetwork: 6,
  acceptOffer: 7,
  declineOffer: 8,
  setRunwayMode: 9,
  answerGrant: 10,
  setStaff: 11,
  takeLoan: 12,
  repayLoan: 13,
  setFees: 14,
  paveArea: 15,
  paintTaxiLine: 16,
  renameAirport: 17,
  cancelContract: 18,
  setNightShift: 19,
  addFacility: 20,
  runCampaign: 21,
  setWages: 22,
  resurfaceRunway: 23,
  moveBuilding: 24,
  setRunwayUsage: 25,
  setJunction: 26,
  dispatchPlows: 27,
  setAdminMode: 28,
  insulateHomes: 29,
  setToll: 30,
  setBigOnly: 31,
} as const satisfies Record<Command['type'], number>;

// Coordinates are signed: the sim, not the codec, decides what is out of bounds.
export function writeCommand(w: BinaryWriter, cmd: Command): void {
  w.u8(CommandId[cmd.type]);
  switch (cmd.type) {
    case 'buyParcel':
      w.i32(cmd.px).i32(cmd.py);
      break;
    case 'placeBuilding':
      w.u16(cmd.building).i32(cmd.x).i32(cmd.y).u8(cmd.rot);
      break;
    case 'upgradeBuilding':
    case 'demolishBuilding':
      w.u32(cmd.id);
      break;
    case 'acceptOffer':
    case 'declineOffer':
      w.u32(cmd.offerId);
      break;
    case 'buildNetwork':
      w.u8(cmd.kind).i32(cmd.x0).i32(cmd.y0).i32(cmd.x1).i32(cmd.y1);
      break;
    case 'removeNetwork':
      w.i32(cmd.area.x0).i32(cmd.area.y0).i32(cmd.area.x1).i32(cmd.area.y1);
      break;
    case 'setRunwayMode':
      // Signed: the sim, not the codec, rejects modes it does not know.
      w.u32(cmd.id).i32(cmd.mode);
      break;
    case 'answerGrant':
      w.u8(cmd.accept ? 1 : 0);
      break;
    case 'setStaff':
      w.u8(cmd.role)
        .i32(cmd.count)
        .u8(cmd.auto ? 1 : 0);
      break;
    case 'takeLoan':
      w.u8(cmd.option);
      break;
    case 'repayLoan':
      w.u32(cmd.loanId);
      break;
    case 'setFees':
      w.u16(cmd.landing).u16(cmd.passenger);
      break;
    case 'paveArea':
      w.i32(cmd.area.x0).i32(cmd.area.y0).i32(cmd.area.x1).i32(cmd.area.y1);
      break;
    case 'paintTaxiLine':
      w.i32(cmd.x0)
        .i32(cmd.y0)
        .i32(cmd.x1)
        .i32(cmd.y1)
        .u8(cmd.remove ? 1 : 0);
      break;
    case 'renameAirport':
      w.string(cmd.name);
      break;
    case 'cancelContract':
      w.u32(cmd.contractId);
      break;
    case 'setNightShift':
      w.u8(cmd.role).u8(cmd.percent);
      break;
    case 'addFacility':
      w.u32(cmd.id).u8(cmd.facility);
      break;
    case 'runCampaign':
      w.u8(cmd.campaign);
      break;
    case 'setWages':
      w.u8(cmd.percent);
      break;
    case 'resurfaceRunway':
      w.u32(cmd.id).u8(cmd.tonight ? 1 : 0);
      break;
    case 'moveBuilding':
      w.u32(cmd.id).i32(cmd.x).i32(cmd.y).u8(cmd.rot);
      break;
    case 'setRunwayUsage':
      // Signed: the sim, not the codec, rejects usages it does not know.
      w.u32(cmd.id).i32(cmd.usage);
      break;
    case 'setJunction':
      w.i32(cmd.x).i32(cmd.y).i32(cmd.kind);
      break;
    case 'dispatchPlows':
      w.u32(cmd.id);
      break;
    case 'setAdminMode':
      w.u8(cmd.on ? 1 : 0);
      break;
    case 'insulateHomes':
      break;
    case 'setToll':
      w.i32(cmd.x)
        .i32(cmd.y)
        .u8(cmd.on ? 1 : 0);
      break;
    case 'setBigOnly':
      w.u32(cmd.id).u8(cmd.on ? 1 : 0);
      break;
  }
}

export function readCommand(r: BinaryReader): Command {
  const id = r.u8();
  switch (id) {
    case CommandId.buyParcel:
      return { type: 'buyParcel', px: r.i32(), py: r.i32() };
    case CommandId.placeBuilding:
      return { type: 'placeBuilding', building: r.u16(), x: r.i32(), y: r.i32(), rot: r.u8() };
    case CommandId.upgradeBuilding:
      return { type: 'upgradeBuilding', id: r.u32() };
    case CommandId.demolishBuilding:
      return { type: 'demolishBuilding', id: r.u32() };
    case CommandId.buildNetwork:
      return {
        type: 'buildNetwork',
        kind: r.u8(),
        x0: r.i32(),
        y0: r.i32(),
        x1: r.i32(),
        y1: r.i32(),
      };
    case CommandId.removeNetwork:
      return {
        type: 'removeNetwork',
        area: { x0: r.i32(), y0: r.i32(), x1: r.i32(), y1: r.i32() },
      };
    case CommandId.acceptOffer:
      return { type: 'acceptOffer', offerId: r.u32() };
    case CommandId.declineOffer:
      return { type: 'declineOffer', offerId: r.u32() };
    case CommandId.setRunwayMode:
      return { type: 'setRunwayMode', id: r.u32(), mode: r.i32() };
    case CommandId.answerGrant:
      return { type: 'answerGrant', accept: r.u8() === 1 };
    case CommandId.setStaff:
      return { type: 'setStaff', role: r.u8(), count: r.i32(), auto: r.u8() === 1 };
    case CommandId.takeLoan:
      return { type: 'takeLoan', option: r.u8() };
    case CommandId.repayLoan:
      return { type: 'repayLoan', loanId: r.u32() };
    case CommandId.setFees:
      return { type: 'setFees', landing: r.u16(), passenger: r.u16() };
    case CommandId.paveArea:
      return {
        type: 'paveArea',
        area: { x0: r.i32(), y0: r.i32(), x1: r.i32(), y1: r.i32() },
      };
    case CommandId.paintTaxiLine:
      return {
        type: 'paintTaxiLine',
        x0: r.i32(),
        y0: r.i32(),
        x1: r.i32(),
        y1: r.i32(),
        remove: r.u8() === 1,
      };
    case CommandId.renameAirport:
      return { type: 'renameAirport', name: r.string() };
    case CommandId.cancelContract:
      return { type: 'cancelContract', contractId: r.u32() };
    case CommandId.setNightShift:
      return { type: 'setNightShift', role: r.u8(), percent: r.u8() };
    case CommandId.addFacility:
      return { type: 'addFacility', id: r.u32(), facility: r.u8() };
    case CommandId.runCampaign:
      return { type: 'runCampaign', campaign: r.u8() };
    case CommandId.setWages:
      return { type: 'setWages', percent: r.u8() };
    case CommandId.resurfaceRunway:
      return { type: 'resurfaceRunway', id: r.u32(), tonight: r.u8() === 1 };
    case CommandId.moveBuilding:
      return { type: 'moveBuilding', id: r.u32(), x: r.i32(), y: r.i32(), rot: r.u8() };
    case CommandId.setRunwayUsage:
      return { type: 'setRunwayUsage', id: r.u32(), usage: r.i32() };
    case CommandId.setJunction:
      return { type: 'setJunction', x: r.i32(), y: r.i32(), kind: r.i32() };
    case CommandId.dispatchPlows:
      return { type: 'dispatchPlows', id: r.u32() };
    case CommandId.setAdminMode:
      return { type: 'setAdminMode', on: r.u8() === 1 };
    case CommandId.insulateHomes:
      return { type: 'insulateHomes' };
    case CommandId.setToll:
      return { type: 'setToll', x: r.i32(), y: r.i32(), on: r.u8() === 1 };
    case CommandId.setBigOnly:
      return { type: 'setBigOnly', id: r.u32(), on: r.u8() === 1 };
    default:
      throw new Error(`Unknown command id ${id}`);
  }
}
